import {
  createListenerMiddleware,
  isAnyOf,
  ListenerEffectAPI,
  ThunkDispatch,
  TypedStartListening
} from '@reduxjs/toolkit'
import {getPaymentRedirectLink} from 'business/bofhLinks'
import {getCreateBookingResult} from 'modules/book/selectors'
import {
  rtqRejectionToRejectResponse,
  rtqResponseWithErrorDataToRejectResponse
} from 'modules/common/actions/error'
import {
  psd2AuthChallenge,
  psd2AuthError,
  psd2AuthSuccess
} from 'modules/psd2Auth/slice'
import {
  BookingLatencyMetric,
  BookingLatencyOutcome,
  markBookingHandoff,
  startBookingAttempt,
  startBookingLatency,
  trackBookingLatency,
  trackBookingTotal
} from 'performance/bookingLatency'
import {preloadConfirmation} from 'performance/preloadConfirmation'
import {AnyAction} from 'redux'
import {getCurrentBookingRequestId} from 'utils/bookingRequestId'
import {urlParams} from 'utils/urlParams'

import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Entity} from '@vio/core/src/analytics/types/Events'
import {Status} from '@vio/core/src/api-types/bovio/response/booking_status'
import {bookApi} from '@vio/core/src/booking/services/bookApi'
import {
  isBookingStatus3DSRequired,
  isBookingStatusErrorResponse
} from '@vio/core/src/booking/utils'

import {
  getBookingCreationStage,
  getIsChargeLaterBookingCreation,
  getRedirectContext
} from './selectors'
import {
  BookingCreationStage,
  reset,
  setRedirectContext,
  startCreating
} from './slice'
import {
  handle3DSChallenge,
  handleBookingConfirmation,
  handleBookingError,
  handleBookingStatusPolling,
  handlePaymentRedirect,
  handlePsd2Success
} from './thunks'

import type {CheckoutThunkExtra, RootState} from 'store'

export const bookingCreationListener = createListenerMiddleware()

type AppDispatch = ThunkDispatch<RootState, CheckoutThunkExtra, AnyAction>
type AppStartListening = TypedStartListening<RootState, AppDispatch>

const startListening =
  bookingCreationListener.startListening as AppStartListening

/** The redirect context is gone after an APM redirect, so the resume URL carries it. */
const getPaymentMethod = (state: RootState) =>
  getRedirectContext(state)?.paymentMethodType ?? urlParams()?.paymentMethodType

const getCreatedBookingId = (state: RootState) =>
  getCreateBookingResult(state).data?.id

/**
 * Times the submission trip from the Complete Booking click. The click handler
 * dispatches this once the form has passed validation, so a click the user has
 * to correct is not measured. Tokenization is still inside the measure, because
 * the user waits through it.
 *
 * A wallet click reaches it only once the Apple Pay or Google Pay sheet has
 * returned a payment method, so wallet measures exclude the time the user
 * spends in that sheet, the same way the submission trip stops at the 3DS
 * overlay rather than waiting for the user to answer it.
 */
startListening({
  actionCreator: setRedirectContext,
  effect: () => {
    startBookingAttempt()
    startBookingLatency({metric: BookingLatencyMetric.Submission})
  }
})

/**
 * Warms the route while the user waits, on whichever document will swap to it.
 * The processing overlay dispatches this for card and wallet, the resume
 * overlay after a payment provider sends the user back. Warming any earlier
 * would buy a PSP round trip against the 2.5s the route swap waits anyway, and
 * would fetch into the outbound document of a redirect that is about to
 * replace it.
 */
startListening({
  predicate: action =>
    trackEvent.match(action) &&
    action.payload.entity === Entity.ConfirmationLoadingScreen &&
    action.payload.action === Action.Displayed,
  effect: () => {
    preloadConfirmation()
  }
})

/**
 * When the booking creation mutation is pending, transition the state machine
 * to `creating`. The charge-later flag is read from the redirect context, which
 * the consumer sets at submission before the mutation fires.
 *
 * This fires from any stage, including `Error` on retry, bypassing `Idle`.
 * Only the 3DS failure path goes through reset() to Idle first, because
 * no new mutation fires to trigger this listener automatically.
 */
startListening({
  matcher: bookApi.endpoints.createBooking.matchPending,
  effect: (_action, listenerApi) => {
    const isChargeLater =
      getRedirectContext(listenerApi.getState())?.isChargeLater ?? false
    listenerApi.dispatch(startCreating({isChargeLater}))
  }
})

/** Terminal stages where the listener should not process further status updates. */
const TERMINAL_STAGES: BookingCreationStage[] = [
  BookingCreationStage.Confirmed,
  BookingCreationStage.Error,
  BookingCreationStage.Redirecting
]

/**
 * Routes booking status poll results to the appropriate thunk:
 * - Error responses → handleBookingError
 * - Payment redirect → handlePaymentRedirect
 * - 3DS required → handle3DSChallenge
 * - Confirmed → handleBookingConfirmation
 */
startListening({
  matcher: bookApi.endpoints.getBookingStatus.matchFulfilled,
  effect: (action, listenerApi: ListenerEffectAPI<RootState, AppDispatch>) => {
    const state = listenerApi.getState()
    const stage = getBookingCreationStage(state)

    if (TERMINAL_STAGES.includes(stage)) return

    const bookingData = action.payload
    const isFinalization = stage === BookingCreationStage.Finalizing

    // Stop polling when booking status response has concluded
    // Continue to handling the results in following lines
    if (
      ![
        Status.Initialized,
        Status.Finalizing,
        Status.ThreeDomainSecureRequired
      ].includes(bookingData.status)
    ) {
      listenerApi.dispatch(handleBookingStatusPolling({shouldPoll: false}))
    }

    if (
      isBookingStatusErrorResponse(bookingData) &&
      bookingData.status === Status.Error
    ) {
      // Error in the booking status response body (must also check status to
      // avoid false positives — responses may carry a null `error` key)
      trackBookingLatency({
        dispatch: listenerApi.dispatch,
        outcome: BookingLatencyOutcome.Errored,
        bookingId: bookingData.id,
        paymentMethod: getPaymentMethod(state)
      })
      listenerApi.dispatch(
        handleBookingError({
          error: rtqResponseWithErrorDataToRejectResponse(bookingData.error),
          isFinalization
        })
      )
      return
    }

    // APM payment redirect — only during initial booking creation.
    // During resume flow (stage = Idle), ResumeFlowCheck handles status
    // polling independently. Processing PaymentRedirected here would redirect
    // the user back to the payment provider again (e.g. Stripe → Revolut Pay).
    if (
      bookingData.status === Status.PaymentRedirected &&
      stage === BookingCreationStage.Creating
    ) {
      const paymentRedirectLink = getPaymentRedirectLink(bookingData.links)
      if (!paymentRedirectLink) return

      const redirectContext = getRedirectContext(state)
      listenerApi.dispatch(
        handlePaymentRedirect({
          paymentRedirectLink,
          paymentMethodType: redirectContext?.paymentMethodType,
          bookingRequestId: getCurrentBookingRequestId(),
          bookingId: bookingData.id
        })
      )
      return
    }

    // 3DS authentication required
    if (
      isBookingStatus3DSRequired(bookingData) &&
      stage === BookingCreationStage.Creating
    ) {
      // stop polling and wait for finalization to restart
      listenerApi.dispatch(handleBookingStatusPolling({shouldPoll: false}))
      listenerApi.dispatch(
        handle3DSChallenge({
          bookingData,
          isChargeLaterBookingCreation: getIsChargeLaterBookingCreation(state),
          bookingStatusLinks: bookingData.links ?? []
        })
      )
      return
    }

    // Booking confirmed
    if (
      bookingData.status === Status.Confirmed ||
      bookingData.status === Status.ChargeLaterConfirmed ||
      bookingData.status === Status.PaymentAuthenticateLaterConfirmed
    ) {
      listenerApi.dispatch(
        handleBookingConfirmation({
          bookingData,
          isWithThreeDomainSecure: isFinalization
        })
      )
    }
  }
})

/**
 * Ends whichever trip is in flight once the confirmation page has its booking
 * on screen. A booking is only done from the user's side when they can see it,
 * so the measure runs to here rather than to the status poll that confirmed it.
 * Reaching this page without a trip open, from a link or a reload, reports
 * nothing.
 */
startListening({
  predicate: action =>
    trackEvent.match(action) &&
    action.payload.entity === Entity.ConfirmationPage &&
    action.payload.action === Action.Displayed,
  effect: (_action, listenerApi) => {
    const bookingId = urlParams()?.bookingId
    const paymentMethod = getPaymentMethod(listenerApi.getState())

    trackBookingLatency({
      dispatch: listenerApi.dispatch,
      outcome: BookingLatencyOutcome.Confirmed,
      bookingId,
      paymentMethod
    })
    trackBookingTotal({
      dispatch: listenerApi.dispatch,
      bookingId,
      paymentMethod
    })
  }
})

/**
 * Ends the submission trip when the 3DS overlay reaches the screen. The psd2
 * middleware dispatches this once the provider is initialized, immediately
 * before it opens the challenge, so bookings that never get that far are
 * excluded. A booking abandoned or declined at the challenge reports nothing
 * further, because its submission is already measured and no resumption began.
 */
startListening({
  actionCreator: psd2AuthChallenge,
  effect: (_action, listenerApi) => {
    const state = listenerApi.getState()
    const bookingId = getCreatedBookingId(state)

    markBookingHandoff(BookingLatencyOutcome.ThreeDomainSecure, bookingId)
    trackBookingLatency({
      dispatch: listenerApi.dispatch,
      outcome: BookingLatencyOutcome.ThreeDomainSecure,
      bookingId,
      paymentMethod: getPaymentMethod(state)
    })
  }
})

/**
 * After a successful 3DS authentication, trigger finalization. The challenge is
 * over, so the resumption trip starts here.
 */
startListening({
  actionCreator: psd2AuthSuccess,
  effect: (_action, listenerApi) => {
    startBookingLatency({
      metric: BookingLatencyMetric.Resumption,
      resumedVia: BookingLatencyOutcome.ThreeDomainSecure
    })
    listenerApi.dispatch(handlePsd2Success())
  }
})

/**
 * When 3DS authentication fails (recoverable error), reset the state machine
 * so the user can resubmit.
 */
startListening({
  actionCreator: psd2AuthError,
  effect: (_action, listenerApi) => {
    listenerApi.dispatch(reset())
  }
})

/**
 * Routes a rejected booking mutation through the same thunk as a status-body
 * error. The two are mutually exclusive: polling only starts once the mutation
 * has resolved.
 */
startListening({
  matcher: isAnyOf(
    bookApi.endpoints.createBooking.matchRejected,
    bookApi.endpoints.bookingFinalization.matchRejected
  ),
  effect: (action, listenerApi) => {
    const state = listenerApi.getState()

    trackBookingLatency({
      dispatch: listenerApi.dispatch,
      outcome: BookingLatencyOutcome.Errored,
      bookingId: getCreatedBookingId(state),
      paymentMethod: getPaymentMethod(state)
    })
    listenerApi.dispatch(
      handleBookingError({
        error: rtqRejectionToRejectResponse(action.payload),
        isFinalization:
          bookApi.endpoints.bookingFinalization.matchRejected(action)
      })
    )
  }
})

startListening({
  matcher: isAnyOf(
    bookApi.endpoints.createBooking.matchFulfilled,
    bookApi.endpoints.bookingFinalization.matchFulfilled
  ),
  effect: (_action, listenerApi) => {
    listenerApi.dispatch(handleBookingStatusPolling({shouldPoll: true}))
  }
})

/**
 * Stop polling when status query does not resolve
 * Usually due to network error
 */
startListening({
  matcher: bookApi.endpoints.getBookingStatus.matchRejected,
  effect: (action, listenerApi) => {
    listenerApi.dispatch(handleBookingStatusPolling({shouldPoll: false}))

    if (!action.payload) return

    listenerApi.dispatch(
      handleBookingError({
        error: rtqRejectionToRejectResponse(action.payload),
        trackOnly: true
      })
    )
  }
})
